import React from "react";
import L from "leaflet";
import {
  AttributionControl,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import { Spin, Tabs, Tooltip } from "antd";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import logisticDashboardService from "../../services/Logitstics/dashboard-service";
import mapBoatIcon from "../../assets/images/map-boat.svg";
import originMarkerIcon from "../../assets/images/origin-marker.svg";
import destinationMarkerIcon from "../../assets/images/destination-marker.svg";
import mapBoxesIcon from "../../assets/images/map-boxes.svg";
import mapCalendarIcon from "../../assets/images/map-calender.svg";
import mapClockIcon from "../../assets/images/map-clock.svg";
import mapMarkerIcon2 from "../../assets/images/map-marker2.svg";
import mapQtyIcon from "../../assets/images/map-qty.svg";
import mapTickIcon from "../../assets/images/map-tick.svg";
import ShipmentSeaRoutePolylines from "./ShipmentSeaRoutePolylines";
import ShipmentAnimatedBoatMarker from "./ShipmentAnimatedBoatMarker";
import carrierLogo from "../../assets/images/ma-container-img.png";
import shipmentNodataIcon from "../../assets/images/ShipmentNodataIcon.svg";
import mapNodataIcon from "../../assets/images/MapNodataIcon.svg";
import "leaflet/dist/leaflet.css";

const { TabPane } = Tabs;

const getShipmentProgressPercent = (overview) => {
  const parsed = Number.parseFloat(overview?.progressPercentage);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.min(100, Math.max(0, parsed));
};

const normalizeShipmentStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const isShipmentDelivered = (overview) => {
  const status = normalizeShipmentStatus(overview?.status);
  if (status.includes("deliver")) return true;
  return getShipmentProgressPercent(overview) >= 100;
};

const hasShipmentShipped = (overview) => {
  if (isMeaningfulValue(overview?.shipementDate)) return true;

  const status = normalizeShipmentStatus(overview?.status);
  if (
    status.includes("transit") ||
    status.includes("progress") ||
    status.includes("shipped")
  ) {
    return true;
  }

  return getShipmentProgressPercent(overview) > 0;
};

const isShipmentInTransit = (overview) => {
  if (!hasShipmentShipped(overview) || isShipmentDelivered(overview)) {
    return false;
  }

  const status = normalizeShipmentStatus(overview?.status);
  if (status.includes("transit") || status.includes("progress")) return true;

  const progress = getShipmentProgressPercent(overview);
  return progress > 0 && progress < 100;
};

const getProgressMilestoneClasses = (overview) => {
  const delivered = isShipmentDelivered(overview);
  const shipped = hasShipmentShipped(overview);
  const inTransit = isShipmentInTransit(overview);

  return {
    shipped: shipped ? "milestone-complete" : "milestone-upcoming",
    inTransit: delivered
      ? "milestone-complete"
      : inTransit
        ? "milestone-active"
        : "milestone-upcoming",
    estimatedDelivery: delivered ? "milestone-complete" : "milestone-upcoming",
  };
};

const SHIPMENT_EMPTY_COPY = {
  overview: {
    title: "Shipment Details Unavailable",
    description:
      "No tracking information is currently available from the tracking provider for this shipment.",
  },
  overviewNoBl: {
    title: "Shipment Details Unavailable",
    description:
      "No tracking information is currently available from the tracking provider for this shipment.",
  },
  map: {
    title: "Route Map Unavailable",
    description:
      "Route information is not available because shipment tracking data has not been provided by the tracking source.",
  },
  vessel: {
    title: "Vessel information unavailable",
    description:
      "Vessel and voyage details are not available for this shipment at this time.",
  },
};

const SHIPMENT_EMPTY_ICONS = {
  shipment: shipmentNodataIcon,
  map: mapNodataIcon,
};

const SHIPMENT_TRACKING_DISCLAIMER =
  "Shipment tracking information is obtained from third-party providers. Knack does not verify its accuracy and disclaims any liability for errors. User discretion advised.";

const isMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim();
  return normalized !== "" && normalized !== "N/A";
};

const hasShipmentOverviewData = (overview) => {
  if (!overview || typeof overview !== "object") return false;

  const fields = [
    overview.eta,
    overview.currentLocation,
    overview.lastUpdated,
    overview.fromLocation,
    overview.toLocation,
    overview.polName,
    overview.podName,
    overview.shipementDate,
    overview.status,
    overview.carrier_no,
    overview.container_Number,
    overview.bL_Number,
    overview.containerDimentions,
  ];

  return fields.some(isMeaningfulValue);
};

const ShipmentTrackingDisclaimer = () => (
  <div className="shipment-tracking-disclaimer" role="note">
    <p>
      <strong>Note :</strong> {SHIPMENT_TRACKING_DISCLAIMER}
    </p>
  </div>
);

const ShipmentEmptyState = ({
  title,
  description,
  className = "",
  variant = "shipment",
}) => (
  <div className={`shipment-empty-state ${className}`.trim()} role="status">
    <span className="shipment-empty-state__icon-wrap" aria-hidden="true">
      <img
        src={SHIPMENT_EMPTY_ICONS[variant] || SHIPMENT_EMPTY_ICONS.shipment}
        alt=""
        className="shipment-empty-state__icon"
      />
    </span>
    <div className="shipment-empty-state__copy">
      <p className="shipment-empty-state__title">{title}</p>
      {description ? <p className="shipment-empty-state__desc">{description}</p> : null}
    </div>
  </div>
);

const OverviewIcon = ({ src, className = "" }) => (
  <img src={src} alt="" className={`shipment-overview-icon ${className}`.trim()} aria-hidden />
);

const formatShipmentStatus = (status) => {
  if (!status) return "N/A";
  return status
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

const formatOverviewDate = (value, format = "DD MMM YYYY") => {
  if (!value) return "N/A";
  const parsed = moment(
    value,
    ["MM/DD/YYYY HH:mm:ss", "MM/DD/YYYY", moment.ISO_8601],
    true
  );
  const date = parsed.isValid() ? parsed : moment(value);
  return date.isValid() ? date.format(format) : value;
};

const formatVesselDate = (value) => {
  if (!value) return "N/A";
  const parsed = moment(
    value,
    ["MM/DD/YYYY HH:mm:ss", "MM/DD/YYYY", moment.ISO_8601],
    true
  );
  const date = parsed.isValid() ? parsed : moment(value);
  return date.isValid() ? date.format("DD MMM YYYY") : "N/A";
};

const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }
  return String(value);
};

const formatScheduleType = (scheduleType) => {
  if (!scheduleType) return "N/A";
  const scheduleTypeMap = {
    A: "Actual",
    E: "Estimated",
  };
  return scheduleTypeMap[scheduleType] || scheduleType;
};

const formatOverviewDateLong = (value) => {
  if (!value) return "N/A";
  const parsed = moment(
    value,
    ["MM/DD/YYYY HH:mm:ss", "MM/DD/YYYY", moment.ISO_8601],
    true
  );
  const date = parsed.isValid() ? parsed : moment(value);
  return date.isValid() ? date.format("DD MMM YYYY") : value;
};

const isAutoCarrierCode = (carrier) =>
  String(carrier || "")
    .trim()
    .toUpperCase() === "AUTO";

const getShipmentTitleParts = (overview) => {
  const carrier = overview?.carrier_no || "";
  const container = overview?.container_Number || overview?.bL_Number || "";

  if (carrier && container && !isAutoCarrierCode(carrier)) {
    return {
      carrier,
      container,
      combined: `${carrier} - ${container}`,
      hasSplitTitle: true,
    };
  }

  return {
    carrier: "",
    container,
    combined: container || overview?.bL_Number || "Shipment",
    hasSplitTitle: false,
  };
};

const buildSummaryMetrics = (overview) => [
  {
    label: "Estimated Time of Arrival",
    value: formatOverviewDate(overview?.eta),
    icon: <OverviewIcon src={mapCalendarIcon} />,
  },
  {
    label: "Current Location",
    value: overview?.currentLocation || "N/A",
    icon: <OverviewIcon src={mapMarkerIcon2} />,
  },
  {
    label: "Last Updated",
    value: formatOverviewDate(overview?.lastUpdated),
    icon: <OverviewIcon src={mapClockIcon} />,
  },
];

const DEFAULT_MAP_CENTER = { lat: 20, lng: 60 };

const formatTimelineDate = (value) => {
  if (!value) return "N/A";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY hh:mm A") : "N/A";
};

const getEventStatusClass = (event) => {
  const status = (event?.eventStatus || "").toLowerCase();
  const diffDays = Number(event?.differenceDays);

  if (status.includes("pending")) return "pending";
  if (status.includes("early") || diffDays < 0) return "early";
  if (status.includes("delay") || diffDays > 0) return "delay";
  if (status.includes("estimated")) return "estimated";
  if (status.includes("arrival")) return "arrival";
  if (status.includes("on time")) return "on-time";
  return "on-time";
};

const formatEventStatusLabel = (event) => {
  if (!event?.eventStatus) return "N/A";

  const status = event.eventStatus;
  const diffDays = Number(event.differenceDays);

  if (status === "On Time") return "On Time";
  if (!Number.isNaN(diffDays) && diffDays > 0) {
    return `Delay by ${diffDays} day${diffDays === 1 ? "" : "s"}`;
  }
  if (!Number.isNaN(diffDays) && diffDays < 0) {
    const earlyDays = Math.abs(diffDays);
    return `Early by ${earlyDays} day${earlyDays === 1 ? "" : "s"}`;
  }

  return status;
};

const mapTimelineEvent = (event) => ({
  label: event.eventName || "N/A",
  tentativeDate: formatTimelineDate(event.eventTimeEstimated),
  actualDate: formatTimelineDate(event.eventTime),
  status: formatEventStatusLabel(event),
  statusClass: getEventStatusClass(event),
  vesselName: event.vesselName || null,
  isLatest: Boolean(event.isLatest),
  recordStatus: String(event.status || "").toUpperCase(),
});

const groupEventsByLocation = (events = []) => {
  if (!events.length) return [];

  const groupedStops = [];

  events.forEach((event) => {
    const location = event.location || "Unknown";
    const lastStop = groupedStops[groupedStops.length - 1];

    if (lastStop && lastStop.locationKey === location) {
      lastStop.events.push(mapTimelineEvent(event));
      if (event.terminal) lastStop.terminal = event.terminal;
      if (event.isLatest) lastStop.hasLatest = true;
      return;
    }

    groupedStops.push({
      locationKey: location,
      location,
      terminal: event.terminal || null,
      hasLatest: Boolean(event.isLatest),
      events: [mapTimelineEvent(event)],
    });
  });

  const currentStopIndex = groupedStops.findIndex((stop) => stop.hasLatest);
  const resolvedCurrentIndex =
    currentStopIndex >= 0 ? currentStopIndex : groupedStops.length - 1;

  return groupedStops.map((stop, stopIndex) => ({
    location: stop.location,
    terminal: stop.terminal,
    state:
      stopIndex < resolvedCurrentIndex
        ? "done"
        : stopIndex === resolvedCurrentIndex
          ? "current"
          : "future",
    highlight: stopIndex === resolvedCurrentIndex,
    events: stop.events,
  }));
};

const toMapPoint = (latitude, longitude) => {
  if (latitude == null || longitude == null) return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
};

const buildMapMarkerData = (mapView) => {
  if (!mapView) return null;

  const origin = toMapPoint(mapView.from?.latitude, mapView.from?.longitude);
  const destination = toMapPoint(mapView.to?.latitude, mapView.to?.longitude);
  const current = toMapPoint(
    mapView.currentLocation?.latitude,
    mapView.currentLocation?.longitude
  );

  if (!origin || !destination) return null;

  return {
    origin,
    destination,
    current,
    bounds: null,
    center: DEFAULT_MAP_CENTER,
    originTitle: mapView.from?.portName || "Origin",
    originPortCode: mapView.from?.portCode || "",
    destinationTitle: mapView.to?.portName || "Destination",
    destinationPortCode: mapView.to?.portCode || "",
    blNumber: mapView.blNumber || mapView.bL_Number || "",
    containerNumber: mapView.containerNumber || mapView.container_Number || "",
    aisImo: mapView.currentLocation?.aisImo || null,
    aisCourse: mapView.currentLocation?.aisCourse ?? null,
    currentSpeed: mapView.currentLocation?.speed ?? null,
    currentTimestamp: mapView.currentLocation?.timestamp || null,
    currentLocationName:
      mapView.currentLocation?.locationName ||
      mapView.currentLocation?.location ||
      mapView.currentLocationName ||
      "",
  };
};

const formatMapCoordinates = (point) => {
  if (!point) return "";
  return `${Number(point.lat).toFixed(4)}°, ${Number(point.lng).toFixed(4)}°`;
};

const formatMapTimestamp = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("ddd, DD MMM YYYY hh:mm A") : "";
};

const formatMapPopupTimestamp = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : "";
};

const SHIPMENT_MAP_INFO_LABELS = {
  origin: "Origin",
  destination: "Destination",
  current: "Current location",
};

const ShipmentMapInfoWindow = ({
  type = "origin",
  placeName = "",
  portCode = "",
  coordinates = "",
  speed = null,
  lastUpdate = "",
}) => {
  const detailRows = [
    portCode ? { label: "Port code", value: portCode } : null,
    speed != null && speed !== "" ? { label: "Speed", value: `${speed} kn` } : null,
    lastUpdate ? { label: "Last update", value: lastUpdate } : null,
    coordinates ? { label: "Coordinates", value: coordinates } : null,
  ].filter(Boolean);

  return (
    <div className={`shipment-map-infowindow shipment-map-infowindow--${type}`}>
      <span className="shipment-map-infowindow__badge">
        {SHIPMENT_MAP_INFO_LABELS[type] || type}
      </span>
      {placeName ? <h4 className="shipment-map-infowindow__place">{placeName}</h4> : null}
      {detailRows.length > 0 ? (
        <ul
          className={`shipment-map-infowindow__details${
            placeName ? "" : " shipment-map-infowindow__details--after-badge"
          }`}
        >
          {detailRows.map((row) => (
            <li className="shipment-map-infowindow__row" key={row.label}>
              <span className="shipment-map-infowindow__label">{row.label}</span>
              <span className="shipment-map-infowindow__value">{row.value}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const ShipmentMapMarker = ({ position, icon, children }) => {
  const markerRef = React.useRef(null);

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={icon}
      eventHandlers={{
        mouseover: () => markerRef.current?.openPopup(),
        mouseout: () => markerRef.current?.closePopup(),
      }}
    >
      {children}
    </Marker>
  );
};

const PORT_MARKER_WIDTH = 16;
const PORT_MARKER_HEIGHT = 24;
const MAP_BOAT_SIZE = 48;

const originMarkerLeafletIcon = L.icon({
  iconUrl: originMarkerIcon,
  iconSize: [PORT_MARKER_WIDTH, PORT_MARKER_HEIGHT],
  iconAnchor: [PORT_MARKER_WIDTH / 2, PORT_MARKER_HEIGHT],
  popupAnchor: [0, -PORT_MARKER_HEIGHT],
});

const destinationMarkerLeafletIcon = L.icon({
  iconUrl: destinationMarkerIcon,
  iconSize: [PORT_MARKER_WIDTH, PORT_MARKER_HEIGHT],
  iconAnchor: [PORT_MARKER_WIDTH / 2, PORT_MARKER_HEIGHT],
  popupAnchor: [0, -PORT_MARKER_HEIGHT],
});

const ShipmentMapLegend = () => (
  <div className="shipment-map-legend" aria-label="Map legend">
    <div className="shipment-map-legend__item">
      <span
        className="shipment-map-legend__line shipment-map-legend__line--tentative"
        aria-hidden="true"
      />
      <span>Tentative Route</span>
    </div>
    <div className="shipment-map-legend__item">
      <span
        className="shipment-map-legend__line shipment-map-legend__line--actual"
        aria-hidden="true"
      />
      <span>Actual Route</span>
    </div>
    <div className="shipment-map-legend__item">
      <span
        className="shipment-map-legend__dot shipment-map-legend__dot--origin"
        aria-hidden="true"
      />
      <span>Origin</span>
    </div>
    <div className="shipment-map-legend__item">
      <span
        className="shipment-map-legend__dot shipment-map-legend__dot--destination"
        aria-hidden="true"
      />
      <span>Destination</span>
    </div>
  </div>
);

const mapBoatLeafletIcon = L.divIcon({
  className: "shipment-map-boat-marker",
  html: `<div class="shipment-map-boat-marker__inner"><img src="${mapBoatIcon}" alt="" /></div>`,
  iconSize: [MAP_BOAT_SIZE, MAP_BOAT_SIZE],
  iconAnchor: [MAP_BOAT_SIZE / 2, MAP_BOAT_SIZE / 2],
});

const FitRouteBounds = ({ bounds }) => {
  const map = useMap();

  React.useEffect(() => {
    if (!bounds) return;
    map.fitBounds(bounds, {
      padding: [32, 32],
      maxZoom: 8,
      animate: false,
    });
  }, [map, bounds]);

  return null;
};

const MapInvalidateSize = ({ isFullscreen }) => {
  const map = useMap();

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();
    }, 150);

    return () => window.clearTimeout(timer);
  }, [map, isFullscreen]);

  return null;
};

const MapRefBridge = ({ onMapReady }) => {
  const map = useMap();

  React.useEffect(() => {
    onMapReady(map);
    return () => onMapReady(null);
  }, [map, onMapReady]);

  return null;
};

const ShipmentMapControls = ({ map, isFullscreen, onToggleFullscreen }) => {
  if (!map) return null;

  return (
    <div className="shipment-map-controls">
      <button
        type="button"
        className="shipment-map-controls__btn"
        onClick={() => map.zoomIn()}
        aria-label="Zoom in"
        title="Zoom in"
      >
        +
      </button>
      <button
        type="button"
        className="shipment-map-controls__btn"
        onClick={() => map.zoomOut()}
        aria-label="Zoom out"
        title="Zoom out"
      >
        −
      </button>
      <button
        type="button"
        className="shipment-map-controls__btn shipment-map-controls__btn--fullscreen"
        onClick={onToggleFullscreen}
        aria-label={isFullscreen ? "Exit full screen" : "View map in full screen"}
        title={isFullscreen ? "Exit full screen" : "Full screen"}
      >
        {isFullscreen ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="M5.5 1H1v4.5M10.5 1H15v4.5M5.5 15H1v-4.5M10.5 15H15v-4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="M1 5.5V1h4.5M15 5.5V1h-4.5M1 10.5V15h4.5M15 10.5V15h-4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

const ShipmentMapBottomUi = ({
  map,
  showLegend,
  isFullscreen,
  onToggleFullscreen,
}) => (
  <div className="shipment-map-bottom-ui">
    {showLegend ? <ShipmentMapLegend /> : null}
    <ShipmentMapControls
      map={map}
      isFullscreen={isFullscreen}
      onToggleFullscreen={onToggleFullscreen}
    />
  </div>
);

const getTimelineConnectorProps = (stop, stopIndex, resolvedCurrentIndex, totalStops) => {
  if (stopIndex < resolvedCurrentIndex) {
    return { connectorClass: "connector-solid" };
  }

  if (stopIndex > resolvedCurrentIndex) {
    return { connectorClass: "connector-dotted" };
  }

  if (stopIndex >= totalStops - 1) {
    return { connectorClass: "connector-solid" };
  }

  const latestIdx = stop.events.findIndex((event) => event.isLatest);
  const eventCount = Math.max(stop.events.length, 1);

  if (latestIdx < 0) {
    return { connectorClass: "connector-dotted" };
  }

  const solidRatio =
    eventCount === 1
      ? 0.75
      : (latestIdx + (latestIdx === eventCount - 1 ? 0.75 : 1)) / eventCount;

  return {
    connectorClass: "connector-partial",
    solidEnd: `${Math.min(solidRatio * 100, 95)}%`,
  };
};

const ShipmentEventTimeline = ({ timelineStops = [], isLoading = false }) => {
  const currentStopIndex = timelineStops.findIndex((stop) => stop.state === "current");
  const resolvedCurrentIndex =
    currentStopIndex >= 0 ? currentStopIndex : Math.max(timelineStops.length - 1, 0);

  return (
    <Spin spinning={isLoading}>
      <div className="shipment-timeline-table-head" aria-hidden="true">
      <div className="timeline-head-main">  
        <h3>Shipment Event Timeline</h3>
        <p>Track the journey of your shipment from origin to destination</p>
      </div>
      <span>Tentative Date</span>
      <span>Actual Date</span>
      <span>Status</span>
    </div>

    {!timelineStops.length && !isLoading ? (
      <p className="shipment-timeline-empty-message">No tracking events available.</p>
    ) : (
    <div className="shipment-route-list">
      {timelineStops.map((stop, stopIndex) => {
        const isActiveNode = stopIndex <= resolvedCurrentIndex;
        const { connectorClass, solidEnd } = getTimelineConnectorProps(
          stop,
          stopIndex,
          resolvedCurrentIndex,
          timelineStops.length
        );

        return (
          <div
            className={`shipment-route-row ${stop.state} ${isActiveNode ? "node-active" : "node-pending"
              } ${connectorClass}`}
            key={`${stop.location}-${stopIndex}`}
            style={{
              "--event-count": stop.events.length,
              ...(solidEnd ? { "--connector-solid-end": solidEnd } : {}),
            }}
          >
            <div className="route-stop">
              <span className="route-node" />
              <strong className={stop.highlight ? "highlight" : ""}>
                {stop.location}
              </strong>
              {stop.terminal ? <p>{stop.terminal}</p> : null}
            </div>
            <div className="route-events">
              {stop.events.map((event, eventIndex) => {
                const isActualRecord = event.recordStatus === "A";

                return (
                  <div
                    className={`route-event${event.isLatest ? " route-event--latest" : ""}${
                      isActualRecord ? " above-latest-row" : ""
                    }`}
                    key={`${event.label}-${event.actualDate}-${eventIndex}`}
                  >
                    <span
                      className={`route-event-label${event.isLatest ? " is-latest" : ""}${
                        isActualRecord && !event.isLatest ? " above-latest-row" : ""
                      }`}
                    >
                      {event.label}
                    </span>
                    <time
                      className={`route-event-date route-event-date--tentative${
                        isActualRecord ? " above-latest-row" : ""
                      }`}
                    >
                      {event.tentativeDate}
                    </time>
                    <time
                      className={`route-event-date route-event-date--actual${
                        isActualRecord ? " above-latest-row" : ""
                      }`}
                    >
                      {event.actualDate}
                    </time>
                    <b className={`status-badge ${event.statusClass}`}>
                      {event.status}
                    </b>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
    )}
    <ShipmentTrackingDisclaimer />
    </Spin>
  );
};

const VESSEL_DETAIL_FIELDS = [
  { label: "Vessel", key: "vesselName" },
  { label: "Voyage", key: "voyageNumber" },
  { label: "Loading", key: "polName" },
  { label: "Discharge", key: "podName" },
  { label: "Actual Time of Departure", key: "etd", format: "date" },
  { label: "Estimated Time of Arrival", key: "eta", format: "date" },
  { label: "Shipment Type", key: "scheduleType", format: "scheduleType" },
  { label: "Priority", key: "priority" },
];

const ShipmentVesselDetails = ({ vesselInfo, isLoading = false }) => {
  const resolveFieldValue = (field) => {
    if (!vesselInfo) return "N/A";

    if (field.format === "date") {
      return formatVesselDate(vesselInfo[field.key]);
    }
    if (field.format === "scheduleType") {
      return formatScheduleType(vesselInfo[field.key]);
    }

    return formatDisplayValue(vesselInfo[field.key]);
  };

  const hasVesselData = VESSEL_DETAIL_FIELDS.some((field) => {
    const value = resolveFieldValue(field);
    return value !== "N/A";
  });

  return (
    <Spin spinning={isLoading}>
      <div className="shipment-vessel-head">
        <h3>Shipment Vessel</h3>
        <p>Comprehensive information about your shipment.</p>
      </div>

      <h4 className="shipment-vessel-section-title">Vessel Details</h4>

      {!hasVesselData && !isLoading ? (
        <ShipmentEmptyState
          className="shipment-empty-state--vessel"
          title={SHIPMENT_EMPTY_COPY.vessel.title}
          description={SHIPMENT_EMPTY_COPY.vessel.description}
        />
      ) : (
        <div className="shipment-vessel-card">
          <div className="row shipment-vessel-grid">
            {VESSEL_DETAIL_FIELDS.map((field) => (
              <div className="col-lg-3 col-md-6 shipment-vessel-field" key={field.key}>
                <div className="shipment-vessel-field-label">{field.label}</div>
                <div className="shipment-vessel-field-value">
                  {resolveFieldValue(field)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Spin>
  );
};

const MapViewTab = ({ bL_Number = "", container_Number = "" }) => {
  const mapPanelRef = React.useRef(null);
  const [isMapFullscreen, setIsMapFullscreen] = React.useState(false);
  const [leafletMap, setLeafletMap] = React.useState(null);
  const handleMapReady = React.useCallback((map) => {
    setLeafletMap(map);
  }, []);
  const [shipmentOverview, setShipmentOverview] = React.useState(null);
  const [shipmentMapView, setShipmentMapView] = React.useState(null);
  const [timelineEvents, setTimelineEvents] = React.useState([]);
  const [isOverviewLoading, setIsOverviewLoading] = React.useState(false);
  const [isMapLoading, setIsMapLoading] = React.useState(false);
  const [isMapRouteLoading, setIsMapRouteLoading] = React.useState(false);
  const [isTimelineLoading, setIsTimelineLoading] = React.useState(false);
  const [vesselInfo, setVesselInfo] = React.useState(null);
  const [isVesselLoading, setIsVesselLoading] = React.useState(false);

  const lastTrackingWarnRef = React.useRef(null);
  const notifyTrackingUnavailable = React.useCallback(() => {
    if (lastTrackingWarnRef.current === bL_Number) return;
    lastTrackingWarnRef.current = bL_Number;
    NotificationManager.warning(
      "No shipment tracking information is currently available for the selected shipment.",
      "Tracking Information Not Available",
      5000
    );
  }, [bL_Number]);

  React.useEffect(() => {
    if (!bL_Number) {
      setShipmentOverview(null);
      return;
    }

    const loadShipmentOverview = async () => {
      setIsOverviewLoading(true);
      try {
        const response = await logisticDashboardService.getShipmentOverviewAPI({
          bL_Number,
          container_Number: container_Number || "",
        });

        const overviewRaw =
          response.data?.statusCode === 200
            ? response.data.responseData?.shipmentOverviewRaw || null
            : null;
        setShipmentOverview(overviewRaw);
        if (!hasShipmentOverviewData(overviewRaw)) {
          notifyTrackingUnavailable();
        }
      } catch (error) {
        setShipmentOverview(null);
        notifyTrackingUnavailable();
        console.log(error);
      } finally {
        setIsOverviewLoading(false);
      }
    };

    loadShipmentOverview();
  }, [bL_Number, container_Number, notifyTrackingUnavailable]);

  React.useEffect(() => {
    if (!bL_Number) {
      setShipmentMapView(null);
      return;
    }

    const loadShipmentMapView = async () => {
      setIsMapLoading(true);
      setShipmentMapView(null);
      try {
        const response = await logisticDashboardService.getShipmentMapViewAPI({
          bL_Number,
          container_Number: container_Number || "",
        });

        if (response.data?.statusCode === 200) {
          setShipmentMapView(response.data.responseData || null);
        } else {
          setShipmentMapView(null);
        }
      } catch (error) {
        setShipmentMapView(null);
        console.log(error);
      } finally {
        setIsMapLoading(false);
      }
    };

    loadShipmentMapView();
  }, [bL_Number, container_Number]);

  React.useEffect(() => {
    if (!bL_Number) {
      setTimelineEvents([]);
      return;
    }

    const loadShipmentTimeline = async () => {
      setIsTimelineLoading(true);
      try {
        const response = await logisticDashboardService.getShipmentTimelineAPI({
          bL_Number,
          container_Number: container_Number || "",
        });

        if (response.data?.statusCode === 200) {
          setTimelineEvents(response.data.responseData?.events || []);
        } else {
          setTimelineEvents([]);
        }
      } catch (error) {
        setTimelineEvents([]);
        console.log(error);
      } finally {
        setIsTimelineLoading(false);
      }
    };

    loadShipmentTimeline();
  }, [bL_Number, container_Number]);

  React.useEffect(() => {
    if (!bL_Number) {
      setVesselInfo(null);
      return;
    }

    const loadVesselInformation = async () => {
      setIsVesselLoading(true);
      try {
        const response = await logisticDashboardService.getVesselInformationAPI({
          bL_Number,
          container_Number: container_Number || "",
        });

        if (response.data?.statusCode === 200) {
          setVesselInfo(response.data.responseData || null);
        } else {
          setVesselInfo(null);
        }
      } catch (error) {
        setVesselInfo(null);
        console.log(error);
      } finally {
        setIsVesselLoading(false);
      }
    };

    loadVesselInformation();
  }, [bL_Number, container_Number]);

  const routeTimelineStops = React.useMemo(
    () => groupEventsByLocation(timelineEvents),
    [timelineEvents]
  );

  const [mapRouteData, setMapRouteData] = React.useState(null);

  React.useEffect(() => {
    if (!shipmentMapView) {
      setMapRouteData(null);
      setIsMapRouteLoading(false);
      return undefined;
    }

    const markerData = buildMapMarkerData(shipmentMapView);
    if (!markerData) {
      setMapRouteData(null);
      setIsMapRouteLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsMapRouteLoading(true);

    const markerBounds = L.latLngBounds([
      [markerData.origin.lat, markerData.origin.lng],
      [markerData.destination.lat, markerData.destination.lng],
      ...(markerData.current
        ? [[markerData.current.lat, markerData.current.lng]]
        : []),
    ]);
    const markerCenter = markerBounds.getCenter();

    setMapRouteData({
      ...markerData,
      bounds: markerBounds,
      center: { lat: markerCenter.lat, lng: markerCenter.lng },
    });

    import("../../helper/seaRoute")
      .then(({ buildSeaRoutesForShipment, getSeaRouteBoundsPoints }) => {
        if (cancelled) return;

        const routes = buildSeaRoutesForShipment(
          markerData.origin,
          markerData.current,
          markerData.destination,
          { aisImo: markerData.aisImo }
        );
        const allPoints = getSeaRouteBoundsPoints(
          markerData.origin,
          markerData.current,
          markerData.destination,
          routes
        );
        const bounds = L.latLngBounds(
          allPoints.map((point) => [point.lat, point.lng])
        );
        const centerPoint = bounds.getCenter();

        setMapRouteData({
          ...markerData,
          bounds,
          center: { lat: centerPoint.lat, lng: centerPoint.lng },
          seaRoutes: routes,
        });
      })
      .catch((error) => {
        console.warn("Sea route bounds calculation failed:", error);
        if (cancelled) return;
        setMapRouteData({
          ...markerData,
          bounds: markerBounds,
          center: { lat: markerCenter.lat, lng: markerCenter.lng },
        });
      })
      .finally(() => {
        if (!cancelled) {
          setIsMapRouteLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shipmentMapView]);

  const mapCenter = mapRouteData?.center || DEFAULT_MAP_CENTER;
  const mapContainerKey = bL_Number || "shipment-map";

  const summaryMetrics = buildSummaryMetrics(shipmentOverview);
  const shipmentTitleParts = getShipmentTitleParts(shipmentOverview);
  const statusLabel = formatShipmentStatus(shipmentOverview?.status);
  const containerLabel = shipmentOverview?.containerDimentions || "N/A";
  const fromLocation =
    shipmentOverview?.fromLocation || shipmentOverview?.polName || "N/A";
  const toLocation =
    shipmentOverview?.toLocation || shipmentOverview?.podName || "N/A";
  const shippedOnLabel = formatOverviewDateLong(shipmentOverview?.shipementDate);
  const etaDeliveryLabel = formatOverviewDateLong(shipmentOverview?.eta);
  const carrierCode = shipmentOverview?.carrier_no || "—";
  const shippedShortDate = formatOverviewDate(
    shipmentOverview?.shipementDate,
    "DD MMM"
  );
  const etaShortDate = formatOverviewDate(shipmentOverview?.eta, "DD MMM");
  const shipmentProgressPercent = getShipmentProgressPercent(shipmentOverview);
  const progressMilestones = getProgressMilestoneClasses(shipmentOverview);
  const hasOverviewData = hasShipmentOverviewData(shipmentOverview);
  const isMapPanelLoading = isMapLoading || isMapRouteLoading;
  const showOverviewEmpty = Boolean(bL_Number && !isOverviewLoading && !hasOverviewData);
  const showMapEmpty = Boolean(bL_Number && !isMapPanelLoading && !mapRouteData);
  const showShipmentTrackingNoData =
    !isOverviewLoading && (!bL_Number || showOverviewEmpty);

  React.useEffect(() => {
    document.body.classList.add("shipment-tracking-page");
    return () => document.body.classList.remove("shipment-tracking-page");
  }, []);

  React.useEffect(() => {
    const onFullscreenChange = () => {
      const panel = mapPanelRef.current;
      setIsMapFullscreen(Boolean(panel && document.fullscreenElement === panel));
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleMapFullscreen = () => {
    const panel = mapPanelRef.current;
    if (!panel) return;

    if (document.fullscreenElement === panel) {
      document.exitFullscreen?.();
      return;
    }

    const request =
      panel.requestFullscreen ||
      panel.webkitRequestFullscreen ||
      panel.msRequestFullscreen;

    request?.call(panel);
  };

  return (
    <div
      className={`shipment-map-view${
        showShipmentTrackingNoData ? " shipment-map-view--no-data" : ""
      }`}
    >
      <div className="shipment-map-top">
        <section className="shipment-summary-panel">
          <Spin spinning={isOverviewLoading}>
            {showShipmentTrackingNoData ? (
              <ShipmentEmptyState
                className="shipment-empty-state--overview"
                variant="shipment"
                title={SHIPMENT_EMPTY_COPY.overview.title}
                description={SHIPMENT_EMPTY_COPY.overview.description}
              />
            ) : (
              <>
          <div className="shipment-summary-head">
            <div className="shipment-brand-mark">
              <img src={carrierLogo} alt={carrierCode} height={48} width={48} />
            </div>
            <div className="shipment-title-block">
              <div className="shipment-title-row">
                <div className="shipment-title-segment">
                  {shipmentOverview?.container_Number ||
                  shipmentTitleParts.container ? (
                    <span className="shipment-title-caption">Container Number</span>
                  ) : null}
                  {shipmentTitleParts.hasSplitTitle ? (
                    <h2 className="shipment-title-heading">
                      <Tooltip title={`Carrier: ${shipmentTitleParts.carrier}`}>
                        <span className="shipment-title-part">{shipmentTitleParts.carrier}</span>
                      </Tooltip>
                      <span className="shipment-title-separator"> - </span>
                      <span className="shipment-title-part">
                        {shipmentTitleParts.container}
                      </span>
                    </h2>
                  ) : (
                    <h2 className="shipment-title-part">{shipmentTitleParts.combined}</h2>
                  )}
                </div>
                <span className="shipment-status-badge">{statusLabel}</span>
              </div>
            </div>
            <div className="shipment-summary-actions">
              <div className="shipment-dimensions-segment">
                <span className="shipment-title-caption">Container Dimensions</span>
                <span className="container-chip">
                  <OverviewIcon src={mapQtyIcon} className="shipment-overview-icon--qty" />
                  {containerLabel}
                </span>
              </div>
            </div>
          </div>

          <div className="shipment-info-grid shipment-info-grid--three">
            {summaryMetrics.map((item) => (
              <div className="shipment-info-item" key={item.label}>
                <span>{item.label}</span>
                <strong>
                  {item.icon}
                  {item.value}
                </strong>
              </div>
            ))}
          </div>

          <div className="shipment-progress-head">
            <strong>Shipment Progress</strong>
            <span>{Math.round(shipmentProgressPercent)}% Complete</span>
          </div>
          <div className="shipment-progress-track">
            <div
              className="shipment-progress-fill"
              style={{ width: `${shipmentProgressPercent}%` }}
            >
              <span className="ship-position" aria-hidden="true">
                <img src={mapBoatIcon} alt="" />
              </span>
            </div>
          </div>
          <div className="shipment-progress-labels">
            <span className={progressMilestones.shipped}>
              <OverviewIcon src={mapBoxesIcon} className="shipment-overview-icon--boxes" />
              Shipped{shippedShortDate !== "N/A" ? `, ${shippedShortDate}` : ""}
            </span>
            <span className={progressMilestones.inTransit}>
              <svg
                width='18'
                height='17'
                viewBox='0 0 18 17'
                fill='currentColor'
                xmlns='http://www.w3.org/2000/svg'
                className="shipment-overview-icon--boxes"
              >
                <path
                  d='M17.3694 14.7006C17.1582 14.4275 16.7663 14.3756 16.4932 14.5862C15.9119 15.0331 15.1232 15.1131 14.4607 14.8406C15.3213 13.2588 15.9238 11.5631 16.2213 9.78063C16.3788 8.8625 15.8569 7.96625 14.9782 7.64875L13.7494 7.21062V4.84562C13.7494 3.9675 13.1263 3.19625 12.2669 3.01187L11.2494 2.79438V1.875C11.2494 0.84125 10.4082 0 9.37443 0H8.12443C7.09068 0 6.24943 0.84125 6.24943 1.875V2.79438L5.2313 3.0125C4.37255 3.19625 3.74943 3.9675 3.74943 4.84625V7.21125L2.51818 7.65063C1.64193 7.9675 1.12005 8.86375 1.27693 9.77875C1.57505 11.5638 2.17693 13.2594 3.03818 14.8419C2.37505 15.115 1.5863 15.0338 1.00568 14.5875C0.731927 14.3763 0.340052 14.4281 0.129427 14.7019C-0.0805728 14.9756 -0.0299479 15.3675 0.244427 15.5781C0.825677 16.0256 1.53755 16.2487 2.25068 16.2487C2.9638 16.2487 3.67505 16.0256 4.2563 15.5781C4.97755 15.0237 6.02318 15.0237 6.74443 15.5781C7.9063 16.4731 9.5938 16.4725 10.7557 15.5781C11.4769 15.0231 12.5226 15.0237 13.2438 15.5781C14.4044 16.4719 16.0926 16.4731 17.2557 15.5781C17.5294 15.3675 17.5807 14.975 17.3707 14.7019L17.3694 14.7006ZM8.1238 1.25H9.3738C9.7188 1.25 9.9988 1.53062 9.9988 1.875V2.57937C9.16693 2.48 8.3313 2.48 7.4988 2.57937V1.875C7.4988 1.53062 7.7788 1.25 8.1238 1.25ZM4.9988 4.84562C4.9988 4.5525 5.20693 4.29562 5.49255 4.23438L6.78568 3.9575C8.08818 3.67875 9.4088 3.67875 10.7113 3.9575L12.0038 4.23375C12.2901 4.29563 12.4982 4.5525 12.4982 4.84562V6.76437L9.82693 5.81125C9.13193 5.56375 8.3663 5.56375 7.67005 5.81125L4.9988 6.76437V4.84562ZM4.12193 14.2206C3.33443 12.7669 2.78255 11.21 2.50818 9.56875C2.45318 9.24875 2.63568 8.93563 2.93943 8.82625L8.08943 6.98875C8.10068 6.985 8.11193 6.98375 8.12318 6.98V14.905C7.90255 14.8356 7.69255 14.7306 7.50443 14.5862C6.53755 13.8431 5.21068 13.7269 4.1213 14.2212L4.12193 14.2206ZM9.99255 14.5862C9.80443 14.7306 9.59443 14.8356 9.3738 14.9044V6.98C9.38505 6.98375 9.3963 6.985 9.40755 6.98875L14.5551 8.825C14.8613 8.93563 15.0438 9.24875 14.9882 9.57188C14.7144 11.2106 14.1632 12.7669 13.3751 14.2206C12.2863 13.7269 10.9594 13.8425 9.99255 14.5862Z'
                  fill='currentColor'
                />
              </svg>
              In Transit
            </span>
            <span className={progressMilestones.estimatedDelivery}>
              <OverviewIcon src={mapTickIcon} />
              Estimated Delivery
              {etaShortDate !== "N/A" ? `, ${etaShortDate}` : ""}
            </span>
          </div>

          <div className="shipment-location-cards">
            <div>
              <label>From</label>
              <span>Origin</span>
              <article>
                <strong>{fromLocation}</strong>
                <p>Shipped on {shippedOnLabel}</p>
              </article>
            </div>
            <div>
              <label>To</label>
              <span>Destination</span>
              <article>
                <strong>{toLocation}</strong>
                <p>Expected by {etaDeliveryLabel}</p>
              </article>
            </div>
          </div>
              </>
            )}
          </Spin>
        </section>

        <section
          ref={mapPanelRef}
          className={`shipment-map-panel${
            showShipmentTrackingNoData || showMapEmpty ? " shipment-map-panel--empty" : ""
          }${isMapFullscreen ? " is-fullscreen" : ""}`}
          aria-label="Shipment route map"
        >
          <Spin spinning={isMapPanelLoading}>
            {showShipmentTrackingNoData || showMapEmpty ? (
              <ShipmentEmptyState
                className="shipment-empty-state--map-panel"
                variant="map"
                title={SHIPMENT_EMPTY_COPY.map.title}
                description={SHIPMENT_EMPTY_COPY.map.description}
              />
            ) : (
              <MapContainer
                key={mapContainerKey}
                className="shipment-leaflet-map"
                center={[mapCenter.lat, mapCenter.lng]}
                zoom={4}
                zoomControl={false}
                attributionControl={false}
                scrollWheelZoom={isMapFullscreen}
              >
                {mapRouteData ? (
                  <>
                    {mapRouteData.bounds ? (
                      <FitRouteBounds bounds={mapRouteData.bounds} />
                    ) : null}
                    <MapRefBridge onMapReady={handleMapReady} />
                    <MapInvalidateSize isFullscreen={isMapFullscreen} />
                    <AttributionControl
                      position="bottomleft"
                      prefix="Leaflet |"
                    />
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <TileLayer
                      attribution='&copy; <a href="https://www.openseamap.org">OpenSeaMap</a>'
                      url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
                    />
                    <ShipmentSeaRoutePolylines
                      origin={mapRouteData.origin}
                      current={mapRouteData.current}
                      destination={mapRouteData.destination}
                      routes={mapRouteData.seaRoutes}
                    />
                    <ShipmentMapMarker
                      position={[mapRouteData.origin.lat, mapRouteData.origin.lng]}
                      icon={originMarkerLeafletIcon}
                    >
                      <Popup
                        className="shipment-map-popup"
                        closeButton={false}
                        offset={[0, -18]}
                      >
                        <ShipmentMapInfoWindow
                          type="origin"
                          placeName={mapRouteData.originTitle}
                          portCode={mapRouteData.originPortCode}
                          coordinates={formatMapCoordinates(mapRouteData.origin)}
                        />
                      </Popup>
                    </ShipmentMapMarker>
                    {mapRouteData.current ? (
                      <ShipmentAnimatedBoatMarker
                        origin={mapRouteData.origin}
                        current={mapRouteData.current}
                        seaRoutes={mapRouteData.seaRoutes}
                        animationKey={mapContainerKey}
                        icon={mapBoatLeafletIcon}
                      >
                        <Popup
                          className="shipment-map-popup"
                          closeButton={false}
                          offset={[0, -20]}
                        >
                          <ShipmentMapInfoWindow
                            type="current"
                            placeName={
                              mapRouteData.currentLocationName ||
                              shipmentOverview?.currentLocation ||
                              ""
                            }
                            speed={mapRouteData.currentSpeed}
                            lastUpdate={formatMapPopupTimestamp(
                              mapRouteData.currentTimestamp
                            )}
                            coordinates={formatMapCoordinates(mapRouteData.current)}
                          />
                        </Popup>
                      </ShipmentAnimatedBoatMarker>
                    ) : null}
                    <ShipmentMapMarker
                      position={[
                        mapRouteData.destination.lat,
                        mapRouteData.destination.lng,
                      ]}
                      icon={destinationMarkerLeafletIcon}
                    >
                      <Popup
                        className="shipment-map-popup"
                        closeButton={false}
                        offset={[0, -18]}
                      >
                        <ShipmentMapInfoWindow
                          type="destination"
                          placeName={mapRouteData.destinationTitle}
                          portCode={mapRouteData.destinationPortCode}
                          coordinates={formatMapCoordinates(mapRouteData.destination)}
                        />
                      </Popup>
                    </ShipmentMapMarker>
                  </>
                ) : (
                  <>
                    <MapRefBridge onMapReady={handleMapReady} />
                    <MapInvalidateSize isFullscreen={isMapFullscreen} />
                    <AttributionControl
                      position="bottomleft"
                      prefix="Leaflet |"
                    />
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                  </>
                )}
              </MapContainer>
            )}
            {!showMapEmpty && leafletMap ? (
              <ShipmentMapBottomUi
                map={leafletMap}
                showLegend={Boolean(mapRouteData)}
                isFullscreen={isMapFullscreen}
                onToggleFullscreen={toggleMapFullscreen}
              />
            ) : null}
          </Spin>
        </section>
      </div>

      {showShipmentTrackingNoData ? (
        <ShipmentTrackingDisclaimer />
      ) : (
        <section className="shipment-timeline-panel">
          <Tabs className="shipment-common-tabs" defaultActiveKey="route" type="card">
            <TabPane tab="Route" key="route">
              <ShipmentEventTimeline
                timelineStops={routeTimelineStops}
                isLoading={isTimelineLoading}
              />
            </TabPane>
            <TabPane tab="Vessel" key="vessel">
              <ShipmentVesselDetails
                vesselInfo={vesselInfo}
                isLoading={isVesselLoading}
              />
            </TabPane>
          </Tabs>
        </section>
      )}
    </div>
  );
};

export default MapViewTab;
